import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/common/Button';
import { ChevronLeft } from 'lucide-react';

interface JoinCodeScreenProps {
  onSubmit: (code: string, displayName: string) => void;
  isLoading?: boolean;
  error?: string | null;
  initialDisplayName?: string;
}

const HEX_CHARS = '0123456789ABCDEF';

/**
 * Join Code entry screen with hex keyboard
 */
export const JoinCodeScreen: React.FC<JoinCodeScreenProps> = ({
  onSubmit,
  isLoading = false,
  error = null,
  initialDisplayName = '',
}) => {
  const [code, setCode] = useState(['', '', '', '']);
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const isCodeComplete = code.every(c => c !== '');
  const isComplete = isCodeComplete && displayName.trim().length > 0;

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = (index: number, value: string) => {
    const char = value.toUpperCase().slice(-1);
    if (char && !HEX_CHARS.includes(char)) return;

    const newCode = [...code];
    newCode[index] = char;
    setCode(newCode);

    // Move to next input
    if (char && index < 3) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      setActiveIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleHexKeyClick = (char: string) => {
    if (activeIndex < 4) {
      const newCode = [...code];
      newCode[activeIndex] = char;
      setCode(newCode);
      
      if (activeIndex < 3) {
        setActiveIndex(activeIndex + 1);
        inputRefs.current[activeIndex + 1]?.focus();
      }
    }
  };

  const handleBackspace = () => {
    if (code[activeIndex]) {
      const newCode = [...code];
      newCode[activeIndex] = '';
      setCode(newCode);
    } else if (activeIndex > 0) {
      const newCode = [...code];
      newCode[activeIndex - 1] = '';
      setCode(newCode);
      setActiveIndex(activeIndex - 1);
      inputRefs.current[activeIndex - 1]?.focus();
    }
  };

  const handleSubmit = () => {
    if (isComplete) {
      onSubmit(code.join(''), displayName.trim());
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header - fixed */}
      <div className="flex-shrink-0 p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-text-secondary hover:text-text-primary transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
          <span>Back</span>
        </button>
      </div>

      {/* Content - scrollable */}
      <div className="flex-1 overflow-auto">
        <div className="flex flex-col items-center justify-center min-h-full p-6 space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text-primary mb-2">ENTER JOIN CODE</h1>
            <p className="text-text-secondary text-sm">
              Ask the hunt organizer for the 4-digit code
            </p>
          </div>

          {/* Code input boxes */}
          <div className="flex gap-3">
            {code.map((char, index) => (
              <input
                key={index}
                ref={(el) => {inputRefs.current[index] = el}}
                type="text"
                inputMode="none"
                value={char}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onFocus={() => setActiveIndex(index)}
                maxLength={1}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
                className={`w-14 h-16 text-center text-2xl font-mono font-bold bg-surface rounded-xl border-2 transition-colors uppercase caret-transparent
                  ${activeIndex === index ? 'border-primary' : 'border-surface-light'}
                  text-text-primary focus:outline-none`}
              />
            ))}
          </div>

          {/* Display Name Input */}
          <div className="w-full max-w-xs">
            <label className="block text-text-secondary text-sm mb-2">Your Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
              maxLength={20}
              className="w-full px-4 py-3 bg-surface border-2 border-surface-light rounded-xl text-text-primary placeholder:text-text-secondary focus:border-primary focus:outline-none"
            />
          </div>

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}

          {/* Join button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={!isComplete || isLoading}
            isLoading={isLoading}
            onClick={handleSubmit}
            className="max-w-xs"
          >
            JOIN
          </Button>
        </div>
      </div>

      {/* Hex keyboard - sticky footer */}
      <div className="flex-shrink-0 p-3 pb-safe-bottom bg-surface border-t border-surface-light">
        <div className="grid grid-cols-8 gap-1.5 max-w-md mx-auto">
          {HEX_CHARS.split('').map((char) => (
            <button
              key={char}
              onClick={() => handleHexKeyClick(char)}
              className="h-10 rounded-lg bg-background text-text-primary font-mono text-base font-semibold hover:bg-surface-light active:scale-95 transition-all"
            >
              {char}
            </button>
          ))}
        </div>
        <div className="flex justify-center gap-3 mt-2 max-w-md mx-auto">
          <button
            onClick={handleBackspace}
            className="flex-1 h-10 rounded-lg bg-background text-text-primary font-medium hover:bg-surface-light active:scale-95 transition-all"
          >
            ⌫
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isComplete || isLoading}
            className="flex-1 h-10 rounded-lg bg-primary text-white font-semibold disabled:opacity-50 active:scale-95 transition-all"
          >
            ✓
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinCodeScreen;
